import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { isPlayerApp } from '../lib/appSurface';
import { usePushSubscription } from '../lib/pwa/push';
import { showErrorToast, showSuccessToast } from '../lib/toast';

// Story 066 — Enable-notifications banner. Mirrors InstallAppBanner from
// 065 but for Web Push: shown to logged-in player-surface users when push
// permission is `default` and push is supported on the platform. iOS
// requires the PWA to be installed before push works at all.

const HIDE_ON_PATHS = new Set([
  '/login',
  '/register',
  '/auth/callback',
  '/auth/confirm',
]);

export function EnableNotificationsBanner() {
  const { t } = useTranslation('common');
  const { user, loading } = useAuth();
  const push = usePushSubscription();
  const { pathname } = useLocation();

  if (!isPlayerApp()) return null;
  if (loading || !user) return null;
  if (HIDE_ON_PATHS.has(pathname)) return null;
  if (push.permission === 'unsupported') return null;
  // Denied is sticky in the browser — we can't re-prompt, so hiding the
  // banner is the only correct UX. Granted-but-not-subscribed is the
  // common case for a new device after the user already enabled on
  // another device; the banner offers a single-click re-subscribe.
  if (push.permission === 'denied') return null;
  if (push.subscribed) return null;
  if (push.ios && !push.installed) return null;
  if (push.dismissed) return null;

  const handleEnable = async () => {
    const r = await push.enable();
    if (r === 'enabled') {
      showSuccessToast(t('enable_notifications_banner.enabled_toast'));
    } else if (r === 'permission-denied') {
      showErrorToast(t('enable_notifications_banner.permission_denied_toast'));
    } else if (r === 'no-vapid-key') {
      showErrorToast(t('enable_notifications_banner.no_vapid_key_toast'));
    } else if (r === 'ios-needs-install') {
      showErrorToast(t('enable_notifications_banner.ios_needs_install_toast'));
    } else if (r === 'unsupported') {
      showErrorToast(t('enable_notifications_banner.unsupported_toast'));
    } else {
      showErrorToast(t('enable_notifications_banner.failed_toast'));
    }
  };

  return (
    <div
      data-testid="enable-notifications-banner"
      className="bg-emerald-900 text-white px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
    >
      <div className="text-sm">
        {t('enable_notifications_banner.body_prefix')}
        <span className="font-semibold">
          {t('enable_notifications_banner.body_emphasis')}
        </span>
        {t('enable_notifications_banner.body_suffix')}
      </div>
      <div className="flex gap-2 items-center">
        <Button
          data-testid="enable-notifications-banner-enable"
          size="sm"
          variant="secondary"
          onClick={handleEnable}
        >
          {t('enable_notifications_banner.enable')}
        </Button>
        <Button
          data-testid="enable-notifications-banner-dismiss"
          size="sm"
          variant="ghost"
          className="text-white hover:bg-emerald-800"
          onClick={push.dismissBanner}
        >
          {t('enable_notifications_banner.dismiss')}
        </Button>
      </div>
    </div>
  );
}
